import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAcceptInvite } from '../api/hooks.js';
import { setWorkspaceId } from '../workspace.js';
import TurnstileGate, { turnstileEnabled } from '../components/TurnstileGate.js';
import { ApiError } from '../api/client.js';

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [name, setName] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const accept = useAcceptInvite();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const needsToken = turnstileEnabled();
  const blocked = needsToken && !turnstileToken;

  if (!token) {
    return (
      <div className="mx-auto mt-24 max-w-md text-center text-sm text-slate-600">
        This invite link is missing its token.
      </div>
    );
  }

  return (
    <div className="mx-auto mt-20 max-w-md px-6">
      <div className="card p-6">
        <h1 className="text-xl font-semibold text-slate-900">Accept your invite</h1>
        <p className="mt-1 text-sm text-slate-600">
          Tell us your name to finish joining the workspace.
        </p>
        <form
          className="mt-5 space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            try {
              const res = await accept.mutateAsync({ token, name, turnstileToken });
              setWorkspaceId(res.workspace.id);
              navigate('/app/dashboard', { replace: true });
            } catch (err) {
              if (err instanceof ApiError) {
                const code = (err.payload as { error?: string })?.error ?? 'invalid_or_expired';
                setError(code === 'turnstile_failed'
                  ? 'Bot check failed. Refresh and try again.'
                  : 'This invite is invalid or expired.');
              } else {
                setError('Something went wrong.');
              }
            }
          }}
        >
          <div>
            <label className="label" htmlFor="name">Your name</label>
            <input
              id="name"
              type="text"
              required
              autoFocus
              className="input mt-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <TurnstileGate onToken={setTurnstileToken} onError={setError} />
          {error && (
            <p className="rounded-md bg-red-50 p-2 text-sm text-red-700">{error}</p>
          )}
          <button
            type="submit"
            disabled={accept.isPending || blocked}
            className="btn-primary w-full"
            title={blocked ? 'Waiting for bot check…' : undefined}
          >
            {accept.isPending ? 'Joining…' : 'Accept invite'}
          </button>
        </form>
      </div>
    </div>
  );
}
